import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import API_BASE_URL, { API_BASE_URL_WITHOUT_API } from '../config/api';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import './Spas.css';
import { AuthContext } from '../context/AuthContext';

// "23 Sep 2026" / "11:02 am" for the Created column
const formatCreatedAt = (value) => {
  if (!value) return { date: '—', time: '' };
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { date: '—', time: '' };
  return {
    date: d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
    time: d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })
  };
};

const Spas = () => {
  const { hasPermission } = useContext(AuthContext);
  const [spas, setSpas] = useState([]);
  const [filteredSpas, setFilteredSpas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [togglingFeatured, setTogglingFeatured] = useState({});
  const [togglingStatus, setTogglingStatus] = useState({});
  const navigate = useNavigate();

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  // ?status=pending (from the dashboard) opens the list already filtered
  const [searchParams] = useSearchParams();
  const initialStatus = ['active', 'inactive', 'pending'].includes(searchParams.get('status')) ? searchParams.get('status') : 'all';
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [featuredFilter, setFeaturedFilter] = useState('all');
  const [verifiedFilter, setVerifiedFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(initialStatus !== 'all');

  useEffect(() => {
    fetchSpas();
  }, []);

  const fetchSpas = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/spas`);
      setSpas(response.data.data || []);
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch spas');
      setLoading(false);
    }
  };

  const applyFilters = useCallback(() => {
    let filtered = [...spas];

    // Search by name
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(spa => 
        spa.name.toLowerCase().includes(query) ||
        (spa.city && spa.city.toLowerCase().includes(query))
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(spa => spa.status === statusFilter);
    }

    // Featured filter
    if (featuredFilter !== 'all') {
      const isFeatured = featuredFilter === 'yes';
      filtered = filtered.filter(spa => spa.featured === isFeatured);
    }

    // Verified filter
    if (verifiedFilter !== 'all') {
      const isVerified = verifiedFilter === 'yes';
      filtered = filtered.filter(spa => spa.verified === isVerified);
    }

    // City filter
    if (cityFilter !== 'all') {
      filtered = filtered.filter(spa => spa.city === cityFilter);
    }

    // Rating filter
    if (ratingFilter !== 'all') {
      const rating = parseInt(ratingFilter);
      filtered = filtered.filter(spa => spa.rating === rating);
    }

    setFilteredSpas(filtered);
  }, [spas, searchQuery, statusFilter, featuredFilter, verifiedFilter, cityFilter, ratingFilter]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const getUniqueCities = () => {
    const cities = [...new Set(spas.map(spa => spa.city).filter(Boolean))];
    return cities.sort();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setFeaturedFilter('all');
    setVerifiedFilter('all');
    setCityFilter('all');
    setRatingFilter('all');
  };

  const hasActiveFilters = () => {
    return searchQuery.trim() !== '' || 
           statusFilter !== 'all' || 
           featuredFilter !== 'all' || 
           verifiedFilter !== 'all' || 
           cityFilter !== 'all' || 
           ratingFilter !== 'all';
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this spa?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/admin/spas/${id}`);
      setSpas(spas.filter(spa => spa._id !== id));
    } catch (error) {
      alert('Failed to delete spa');
    }
  };

  const handleToggleFeatured = async (spaId, currentFeaturedStatus) => {
    if (!currentFeaturedStatus) {
      // Enabling featured requires subscription - redirect to edit page
      if (window.confirm('Featured tag requires a subscription. Would you like to edit this spa to select a subscription?')) {
        navigate(`/sy-admin/spas/edit/${spaId}`);
      }
      return;
    }
    
    // Disabling featured - no subscription needed
    setTogglingFeatured(prev => ({ ...prev, [spaId]: true }));
    
    try {
      const response = await axios.put(`${API_BASE_URL}/admin/spas/${spaId}`, {
        featured: false
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Update the spa in the local state
      setSpas(spas.map(spa => 
        spa._id === spaId 
          ? { ...spa, featured: response.data.data.featured }
          : spa
      ));
    } catch (error) {
      console.error('Error toggling featured status:', error);
      alert('Failed to update featured status');
    } finally {
      setTogglingFeatured(prev => ({ ...prev, [spaId]: false }));
    }
  };

  const handleToggleVerified = async (spaId, currentVerifiedStatus) => {
    if (!currentVerifiedStatus) {
      // Enabling verified requires subscription - redirect to edit page
      if (window.confirm('Verified tag requires a subscription. Would you like to edit this spa to select a subscription?')) {
        navigate(`/sy-admin/spas/edit/${spaId}`);
      }
      return;
    }
    
    // Disabling verified - no subscription needed
    setTogglingFeatured(prev => ({ ...prev, [spaId]: true }));
    
    try {
      const response = await axios.put(`${API_BASE_URL}/admin/spas/${spaId}`, {
        verified: false
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Update the spa in the local state
      setSpas(spas.map(spa => 
        spa._id === spaId 
          ? { ...spa, verified: response.data.data.verified }
          : spa
      ));
    } catch (error) {
      console.error('Error toggling verified status:', error);
      alert('Failed to update verified status');
    } finally {
      setTogglingFeatured(prev => ({ ...prev, [spaId]: false }));
    }
  };

  const handleToggleStatus = async (spaId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    setTogglingStatus(prev => ({ ...prev, [spaId]: true }));
    
    try {
      const response = await axios.put(`${API_BASE_URL}/admin/spas/${spaId}`, {
        status: newStatus
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Update the spa in the local state
      setSpas(spas.map(spa => 
        spa._id === spaId 
          ? { ...spa, status: response.data.data.status }
          : spa
      ));
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('Failed to update spa status');
    } finally {
      setTogglingStatus(prev => ({ ...prev, [spaId]: false }));
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return `${API_BASE_URL_WITHOUT_API}${imagePath}`;
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active':
        return 'status-badge status-active';
      case 'inactive':
        return 'status-badge status-inactive';
      case 'pending':
        return 'status-badge status-pending';
      default:
        return 'status-badge';
    }
  };

  const pagination = usePagination(filteredSpas, { noun: 'spas' });

  if (loading) {
    return (
      <Layout>
        <div className="spas-container">
          <div className="spas-header">
            <div className="spas-header-content">
              <h1>Spas</h1>
              <p className="spas-subtitle">Manage your spa listings</p>
            </div>
            {hasPermission('spas.create') && (
              <Link to="/sy-admin/spas/new" className="btn btn-primary btn-add">
                <span className="btn-icon">+</span>
                Add New Spa
              </Link>
            )}
          </div>
          <div className="spas-table-container">
            <table className="spas-table">
              <thead>
                <tr>
                  <th>Joined</th>
                  <th>Image</th>
                  <th>Spa Name</th>
                  <th>Phone</th>
                  <th>Actions</th>
                  <th>Flags</th>
                  <th>Status</th>
                  <th>City</th>
                  <th>Address</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((index) => (
                  <tr key={index} className="skeleton-row">
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '90px', height: '14px' }}></div>
                      <div className="skeleton skeleton-text" style={{ width: '60px', height: '12px', marginTop: '6px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-image" style={{ width: '60px', height: '60px', borderRadius: '8px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '60%', height: '16px' }}></div>
                    </td>
                    <td><div className="skeleton skeleton-text" style={{ width: '100px', height: '14px' }}></div></td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div className="skeleton skeleton-button" style={{ width: '36px', height: '36px', borderRadius: '8px' }}></div>
                        <div className="skeleton skeleton-button" style={{ width: '36px', height: '36px', borderRadius: '8px' }}></div>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div className="skeleton skeleton-text" style={{ width: '70px', height: '20px', borderRadius: '4px' }}></div>
                        <div className="skeleton skeleton-text" style={{ width: '70px', height: '20px', borderRadius: '4px' }}></div>
                      </div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-switch" style={{ width: '100px', height: '26px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '50%', height: '14px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '90%', height: '14px' }}></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="spas-container">
        <div className="spas-header">
          <div className="spas-header-content">
            <h1>Spas</h1>
            <p className="spas-subtitle">Manage your spa listings</p>
          </div>
          {hasPermission('spas.create') && (
            <Link to="/sy-admin/spas/new" className="btn btn-primary btn-add">
              <span className="btn-icon">+</span>
              Add New Spa
            </Link>
          )}
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        {/* Filter Section */}
        {spas.length > 0 && (
          <div className="filters-section">
            <div className="filters-header">
              <button 
                className="filters-toggle-btn"
                onClick={() => setShowFilters(!showFilters)}
              >
                <span className="filters-icon">🔍</span>
                Filters
                {hasActiveFilters() && <span className="active-filters-badge">{Object.values({
                  search: searchQuery.trim(),
                  status: statusFilter,
                  featured: featuredFilter,
                  verified: verifiedFilter,
                  city: cityFilter,
                  rating: ratingFilter
                }).filter(v => v !== '' && v !== 'all').length}</span>}
                <span className="toggle-arrow">{showFilters ? '▲' : '▼'}</span>
              </button>
              {hasActiveFilters() && (
                <button className="clear-filters-btn" onClick={clearFilters}>
                  Clear All
                </button>
              )}
            </div>

            {showFilters && (
              <div className="filters-content">
                <div className="filter-row">
                  <div className="filter-group">
                    <label className="filter-label">Search</label>
                    <input
                      type="text"
                      className="filter-input"
                      placeholder="Search by name or city..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>

                  <div className="filter-group">
                    <label className="filter-label">Status</label>
                    <select
                      className="filter-select"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <label className="filter-label">Featured</label>
                    <select
                      className="filter-select"
                      value={featuredFilter}
                      onChange={(e) => setFeaturedFilter(e.target.value)}
                    >
                      <option value="all">All</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <label className="filter-label">Verified</label>
                    <select
                      className="filter-select"
                      value={verifiedFilter}
                      onChange={(e) => setVerifiedFilter(e.target.value)}
                    >
                      <option value="all">All</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                </div>

                <div className="filter-row">
                  <div className="filter-group">
                    <label className="filter-label">City</label>
                    <select
                      className="filter-select"
                      value={cityFilter}
                      onChange={(e) => setCityFilter(e.target.value)}
                    >
                      <option value="all">All Cities</option>
                      {getUniqueCities().map(city => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label className="filter-label">Rating</label>
                    <select
                      className="filter-select"
                      value={ratingFilter}
                      onChange={(e) => setRatingFilter(e.target.value)}
                    >
                      <option value="all">All Ratings</option>
                      <option value="5">5 Stars</option>
                      <option value="4">4 Stars</option>
                      <option value="3">3 Stars</option>
                      <option value="2">2 Stars</option>
                      <option value="1">1 Star</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {hasActiveFilters() && (
              <div className="filter-results-info">
                Showing {filteredSpas.length} of {spas.length} spas
              </div>
            )}
          </div>
        )}

        {spas.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">💆</div>
            <h2>No spas found</h2>
            <p>Get started by adding your first spa to the platform.</p>
            {hasPermission('spas.create') && (
              <Link to="/sy-admin/spas/new" className="btn btn-primary">
                Add Your First Spa
              </Link>
            )}
          </div>
        ) : (
          <div className="spas-table-container">
            {filteredSpas.length === 0 && hasActiveFilters() ? (
              <div className="empty-state">
                <div className="empty-state-icon">🔍</div>
                <h2>No spas match your filters</h2>
                <p>Try adjusting your filter criteria or clear all filters to see all spas.</p>
                <button onClick={clearFilters} className="btn btn-primary">
                  Clear Filters
                </button>
              </div>
            ) : (
              <table className="spas-table">
                <thead>
                  <tr>
                    <th>Joined</th>
                    <th>Image</th>
                    <th>Spa Name</th>
                    <th>Phone</th>
                    <th>Actions</th>
                    <th>Flags</th>
                    <th>Status</th>
                    <th>City</th>
                    <th>Address</th>
                  </tr>
                </thead>
                <tbody>
                  {pagination.items.map((spa) => {
                    const isTogglingStatus = togglingStatus[spa._id];
                    const featuredImage = spa.featuredImage || (spa.images && spa.images[0]);
                    const imageUrl = getImageUrl(featuredImage);
                    const phoneNumber = spa.phone || spa.whatsappNumber || '';
                    const createdAt = formatCreatedAt(spa.createdAt);
                    const address = spa.displayAddress || spa.address || 'N/A';
                    
                    return (
                      <tr key={spa._id}>
                        <td>
                          <div className="hotel-created-cell">
                            <span className="created-date">{createdAt.date}</span>
                            <span className="created-time">{createdAt.time}</span>
                          </div>
                        </td>
                        <td>
                          <div className="spa-image-cell">
                            {imageUrl ? (
                              <img src={imageUrl} alt={spa.name} className="spa-table-image" />
                            ) : (
                              <div className="spa-image-placeholder">
                                <span className="placeholder-icon-small">💆</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="spa-name-cell">
                            <span className="spa-name-text">{spa.name}</span>
                          </div>
                        </td>
                        <td>
                          <div className="hotel-phone-cell">
                            {phoneNumber ? <a href={`tel:${phoneNumber}`}>{phoneNumber}</a> : <span className="category-empty">—</span>}
                          </div>
                        </td>
                        <td>
                          <div className="table-actions">
                            <Link
                              to={`/sy-admin/spas/view/${spa._id}`}
                              className="btn btn-edit btn-icon-only"
                              title="View Spa"
                            >
                              <span className="btn-icon">👁️</span>
                            </Link>
                            {hasPermission('spas.update') && (
                              <Link
                                to={`/sy-admin/spas/edit/${spa._id}`}
                                className="btn btn-edit btn-icon-only"
                                title="Edit Spa"
                              >
                                <span className="btn-icon">✏️</span>
                              </Link>
                            )}
                            {hasPermission('spas.delete') && (
                              <button
                                onClick={() => handleDelete(spa._id)}
                                className="btn btn-delete btn-icon-only"
                                title="Delete Spa"
                              >
                                <span className="btn-icon">🗑️</span>
                              </button>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="spa-flags-cell">
                            <div className="flags-container">
                              {spa.featured ? (
                                <span className="flag-item flag-featured" title="Featured">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
                                  </svg>
                                  <span>Featured</span>
                                </span>
                              ) : (
                                <span className="flag-item flag-inactive">—</span>
                              )}
                              {spa.verified ? (
                                <span className="flag-item flag-verified" title="Verified">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="currentColor"/>
                                  </svg>
                                  <span>Verified</span>
                                </span>
                              ) : (
                                <span className="flag-item flag-inactive">—</span>
                              )}
                              {spa.showOnHome && (
                                <span className="flag-item flag-home" title="Shown on the app home screen without the Featured tag">
                                  <span>🏠 Home</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="status-cell">
                            <label className="status-toggle-label">
                              <div className="toggle-switch">
                                <input
                                  type="checkbox"
                                  checked={spa.status === 'active'}
                                  onChange={() => handleToggleStatus(spa._id, spa.status)}
                                  disabled={isTogglingStatus}
                                  className="toggle-input"
                                />
                                <span className={`toggle-slider ${isTogglingStatus ? 'loading' : ''}`}></span>
                              </div>
                              <span className={`status-text ${spa.status === 'active' ? 'status-active-text' : 'status-inactive-text'}`}>
                                {spa.status === 'active' ? 'Active' : spa.status === 'inactive' ? 'Inactive' : 'Pending'}
                              </span>
                            </label>
                          </div>
                        </td>
                        <td>
                          <div className="spa-city-cell">
                            <span className="location-icon">📍</span>
                            <span>{spa.city || 'N/A'}</span>
                          </div>
                        </td>
                        <td>
                          <div className="spa-address-cell">
                            <span className="address-text" title={address}>
                              {address}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            <Pagination {...pagination.props} />
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Spas;
